import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { X, Search, Link as LinkIcon, Building2, CheckCircle, Unlink } from 'lucide-react';

const ServiceLinkPicker = ({ carId, onClose, language = 'en' }) => {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [existingLink, setExistingLink] = useState(null);

  // Helper to convert timestamp IDs ("1771276869103") to Postgres UUID
  const toUUID = (localId) => {
    if (String(localId).includes('-')) return localId;
    let hex = '';
    for (let i = 0; i < String(localId).length; i++) {
      hex += String(localId).charCodeAt(i).toString(16);
    }
    hex = hex.padEnd(32, '0').slice(0, 32);
    return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20,32)}`;
  };

  useEffect(() => {
    loadServicesAndLink();
  }, [carId]);

  const loadServicesAndLink = async () => {
    setLoading(true);
    try {
      // Fetch approved services
      const { data: servicesData, error: svcError } = await supabase
        .from('service_applications')
        .select('user_id, business_name')
        .eq('status', 'approved');
      
      if (svcError) throw svcError;
      setServices(servicesData || []);

      const uuidCarId = toUUID(carId);

      // Check for existing link for this car
      const { data: linkData, error: linkError } = await supabase
        .from('car_service_links')
        .select('*')
        .eq('car_id', uuidCarId)
        .maybeSingle();
        
      if (!linkError && linkData) {
        setExistingLink(linkData);
      }
    } catch (err) {
      console.error('Error loading services:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestLink = async (serviceId) => {
    if (!user) {
      setErrorMsg('You must be signed in to an account to link your car.');
      return;
    }
    setRequesting(true);
    setErrorMsg('');
    try {
      const uuidCarId = toUUID(carId);

      // Upsert the car to Supabase so the Service Account can see it
      const localCars = JSON.parse(localStorage.getItem('cars') || '[]');
      const carToLink = localCars.find(c => c.id === carId);
      
      if (carToLink) {
        await supabase.from('cars').upsert({
          id: uuidCarId,
          user_id: user.id,
          name: carToLink.name,
          plate_number: carToLink.plateNumber || '',
          color: carToLink.color || '#3b82f6',
          total_mileage_km: carToLink.total_mileage_km || 0
        });
      }

      const { error } = await supabase
        .from('car_service_links')
        .insert({
          car_id: uuidCarId,
          service_id: serviceId,
          status: 'pending'
        });

      if (error) throw error;
      setSuccessMsg('Request sent successfully!');
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Insert error:', err);
      if (err.code === '23505') {
        setErrorMsg('You have already requested a link to this service.');
      } else {
        setErrorMsg(`Failed: ${err.message || err.details || 'Unknown error'}`);
      }
    } finally {
      setRequesting(false);
    }
  };

  const handleUnlink = async () => {
    if (!existingLink) return;
    setUnlinking(true);
    setErrorMsg('');
    try {
      const { error } = await supabase
        .from('car_service_links')
        .delete()
        .eq('id', existingLink.id);
      if (error) throw error;
      setExistingLink(null);
      setSuccessMsg('Car unlinked successfully!');
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setErrorMsg(`Failed to unlink: ${err.message || 'Unknown error'}`);
    } finally {
      setUnlinking(false);
    }
  };

  const filteredServices = services.filter(s => 
    s.business_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-primary)',
        width: '90%', maxWidth: '420px',
        borderRadius: '24px',
        margin: '0 auto',
        padding: '1.5rem', maxHeight: '75vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <LinkIcon size={20} /> Link Auto Service
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>

        {existingLink ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', marginTop: '1rem' }}>
            <div style={{ 
              width: '48px', height: '48px', borderRadius: '50%', 
              background: existingLink.status === 'approved' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(234, 179, 8, 0.1)',
              color: existingLink.status === 'approved' ? '#22c55e' : '#eab308',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <CheckCircle size={24} />
            </div>
            <h3 style={{ margin: '0 0 0.5rem' }}>
              {existingLink.status === 'approved' ? 'Car is Linked' : 'Request Pending'}
            </h3>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              {existingLink.status === 'approved' 
                ? 'Your car is currently linked to an auto service. They can easily view your vehicle data and add maintenance logs.' 
                : 'Your link request has been sent and is awaiting approval from the service station.'}
            </p>
            {errorMsg && (
              <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.75rem', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', fontSize: '0.82rem' }}>
                {errorMsg}
              </div>
            )}
            {successMsg && (
              <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.75rem', background: '#dcfce7', color: '#166534', borderRadius: '8px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <CheckCircle size={14} /> {successMsg}
              </div>
            )}
            <button
              onClick={handleUnlink}
              disabled={unlinking}
              style={{
                marginTop: '1rem',
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(239, 68, 68, 0.08)',
                color: '#ef4444',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                borderRadius: '10px',
                fontWeight: 600,
                cursor: unlinking ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem',
                opacity: unlinking ? 0.6 : 1,
              }}
            >
              <Unlink size={16} />
              {unlinking ? 'Unlinking...' : 'Unlink from Service'}
            </button>
          </div>
        ) : (
          <>
            <p style={{ margin: '0 0 1rem', color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.5 }}>
              Link your car to an approved auto service business so they can seamlessly pull up your details without searching for your plate number.
            </p>

            <div style={{ position: 'relative', marginBottom: '1rem' }}>
              <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
              <input
                type="text"
                placeholder="Search business name..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  width: '100%', padding: '0.85rem 1rem 0.85rem 2.5rem',
                  borderRadius: '12px', border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {errorMsg && (
              <div style={{ padding: '0.75rem', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem' }}>
                {errorMsg}
              </div>
            )}
            
            {successMsg && (
              <div style={{ padding: '0.75rem', background: '#dcfce7', color: '#166534', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckCircle size={16} /> {successMsg}
              </div>
            )}

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading services...</div>
              ) : filteredServices.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No services found.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {filteredServices.map(svc => (
                    <div key={svc.user_id} style={{ 
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '12px',
                      border: '1px solid var(--border-color)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ 
                          width: '40px', height: '40px', borderRadius: '10px', 
                          background: '#738ADB',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <Building2 size={20} color="white" />
                        </div>
                        <div style={{ fontWeight: 600 }}>{svc.business_name}</div>
                      </div>
                      <button 
                        onClick={() => handleRequestLink(svc.user_id)}
                        disabled={requesting}
                        style={{
                          padding: '0.5rem 1rem', background: 'rgba(99, 102, 241, 0.1)',
                          color: '#738ADB', border: 'none', borderRadius: '8px',
                          fontWeight: 600, cursor: requesting ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {requesting ? '...' : 'Request'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceLinkPicker;
